import React, { useState, useEffect } from "react";
import { LICENSE_TYPES } from "../../types/licenseTypes";
import {
  License,
  CreateLicenseData,
  EditLicenseData,
  User,
  Company,
  Project,
} from "../../store/types";
import { useGetQuery } from "../../store/slices/apiSlice";
import { validateLicenseField } from "../../hooks/validation";
import { formatDate, toUTCStartOfDay } from "../../hooks/dateTimeConvertion";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import CustomInputNumberIncrement from "../CustomInput/CustomInputNumberIncrement";
import CustomDropdown from "../CustomInput/CustomDropdown";
import CustomCalendar from "../CustomInput/CustomCalendar";
import { StyledIconLine } from "../StyledIcon/StyledIcon";
import { BodyText, DangerText } from "../../styles/Typography";
import {
  IconPlusBlack,
  IconCalendar,
  IconDocument,
  IconCompany,
  IconCalendarCheckFilled,
  IconCalendarXFilled,
  IconDocFilled,
  IconCompanyFilled,
  IconCaseFilled,
  IconCase,
  IconUserFilled,
  IconUser,
} from "../../assets/icons/Icon";
import { Column, SmallColumn, Row } from "./Dialogs.styles";

interface DialogProps {
  visible: boolean;
  onHide: () => void;
  dialogType: string;
  user?: User;
  company?: Company;
  project?: Project;
  license?: License | null;
  enrichedLicense?:
    | (License & {
        projectName?: string;
        organizationName?: string;
        userName?: string;
        licenseType?: string;
      })
    | null;
  handleActivateLicense?: (id: string) => Promise<boolean>;
  handleDeactivateLicense?: (id: string) => Promise<boolean>;
  handleAddLicense?: (
    license: CreateLicenseData
  ) => Promise<{ success: boolean; licenses?: License[] }>;
  handleEditLicense?: (
    license: EditLicenseData,
    id: string
  ) => Promise<boolean>;
  handleEditLicenseUser?: (id: string, userId: string) => Promise<boolean>;
  handleDeleteLicense?: (license: License) => Promise<boolean>;
  refetchData?: () => {};
  usersList?: User[] | null;
  usersOptions?: { label: string; value: any }[];
  companiesOptions?: { label: string; value: any }[];
  projectsOptions?: { label: string; value: any }[];
  licensesEP: string;
}

const initialFormData: CreateLicenseData = {
  numberOfLicenses: 1,
  type: "",
  projectId: "",
  organizationId: 0,
  startDate: null,
  expirationDate: null,
};

const initialErrors: Record<keyof CreateLicenseData, boolean> = {
  numberOfLicenses: false,
  type: false,
  projectId: false,
  organizationId: false,
  startDate: false,
  expirationDate: false,
};

const LicenseDialogs: React.FC<DialogProps> = ({
  visible,
  onHide,
  dialogType,
  user,
  company,
  project,
  license,
  enrichedLicense,
  handleActivateLicense,
  handleDeactivateLicense,
  handleAddLicense,
  handleEditLicense,
  handleEditLicenseUser,
  handleDeleteLicense,
  refetchData,
  usersList = [],
  usersOptions = [],
  companiesOptions = [],
  projectsOptions = [],
  licensesEP,
}) => {
  const [localLicense, setLocalLicense] = useState<License | null>(null);
  const [formData, setFormData] = useState<CreateLicenseData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateLicenseData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [addUserOpt, setAddUserOpt] = useState<{ label: string; value: any }[]>(
    []
  );
  const [isAvailableUsers, setIsAvailableUsers] = useState<boolean>(true);
  const [isAddUser, setIsAddUser] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  const { data: fetchedLicense, refetch: refetchLicense } = useGetQuery(
    `${licensesEP}/${license?.id}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !visible || !license?.id || dialogType !== "edit",
    }
  );

  const handleFieldChange = (field: keyof CreateLicenseData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    if (dialogType !== "create") return true;

    const newErrors: Record<keyof CreateLicenseData, boolean> = {
      numberOfLicenses: !validateLicenseField(
        "numberOfLicenses",
        formData.numberOfLicenses
      ),
      type: !validateLicenseField("type", formData.type),
      projectId: !validateLicenseField("projectId", formData.projectId),
      organizationId: !validateLicenseField(
        "organizationId",
        formData.organizationId
      ),
      startDate: !validateLicenseField("startDate", formData.startDate),
      expirationDate: !validateLicenseField(
        "expirationDate",
        formData.expirationDate
      ),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateLicenseData) => {
    const value = formData[field];
    const isValid = validateLicenseField(field, value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (dialogType !== "delete") {
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }
    }

    setValidForm(true);
    setSubmitting(true);
    let success = false;

    try {
      if (dialogType === "edit" && isAddUser && handleEditLicenseUser) {
        if (!selectedUser) {
          setValidForm(false);
          return;
        }
        success = await handleEditLicenseUser!(license!.id, selectedUser);
      } else {
        const dataToSend = {
          ...formData,
          startDate: toUTCStartOfDay(formData.startDate),
          expirationDate: toUTCStartOfDay(formData.expirationDate),
        };

        switch (dialogType) {
          case "create":
            const addResult = await handleAddLicense!(dataToSend as any);
            success = addResult.success;
            if (success && user && addResult.licenses) {
              for (const license of addResult.licenses) {
                await handleEditLicenseUser!(license.id, user.id);
              }
            }
            break;
          case "edit": {
            const editData: EditLicenseData = {
              startDate: toUTCStartOfDay(formData.startDate) as any,
              expirationDate: toUTCStartOfDay(formData.expirationDate) as any,
              comments: comment,
            };
            success = await handleEditLicense!(editData, license!.id);
            break;
          }
          case "delete":
            success = await handleDeleteLicense!(license!);
            break;
          case "enable":
            success = await handleActivateLicense!(license!.id);
            break;
          case "disable":
            success = await handleDeactivateLicense!(license!.id);
            break;
          case "addUser":
            if (!selectedUser) {
              return;
            }
            success = await handleEditLicenseUser!(license!.id, selectedUser);
            break;
          default:
            success = false;
        }
      }

      if (success) {
        refetchData!();
        setLocalLicense(null);
        onHide();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleHide = () => {
    setLocalLicense(null);
    onHide();
  };

  useEffect(() => {
    if (fetchedLicense) {
      setLocalLicense(fetchedLicense);
    }
  }, [fetchedLicense, dialogType, visible]);

  useEffect(() => {
    if (visible) {
      setValidForm(true);
      if (dialogType === "edit" && localLicense) {
        setFormData({
          numberOfLicenses: localLicense.numberOfLicenses || 0,
          type: localLicense.type || "",
          projectId: localLicense.projectId || "",
          organizationId: localLicense.organizationId || 0,
          startDate: localLicense.startDate
            ? new Date(localLicense.startDate)
            : null,
          expirationDate: localLicense.expirationDate
            ? new Date(localLicense.expirationDate)
            : null,
        });
        setErrors(initialErrors);
        setComment(localLicense.comments || "");
      } else if (dialogType === "create") {
        setFormData({
          ...initialFormData,
          projectId: project?.id ?? "",
          organizationId: company?.id ?? 0,
        });
        setErrors(initialErrors);
        setComment("");
      } else {
        setFormData(initialFormData);
        setErrors(initialErrors);
        setComment("");
      }
    }
  }, [visible, dialogType, localLicense]);

  useEffect(() => {
    if (!visible) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setValidForm(true);
      setSubmitting(false);
      setLocalLicense(null);
      setComment("");
      setAddUserOpt([]);
      setSelectedUser("");
      setIsAddUser(false);
    }
  }, [visible]);

  useEffect(() => {
    if (
      (dialogType === "addUser" || isAddUser) &&
      license?.organizationId &&
      usersList
    ) {
      const filtered = usersList
        .filter((user) => user.organizationId === license.organizationId)
        .map((user) => ({
          label: `${user.lastName ?? ""} ${user.name ?? ""} ${
            user.patronymic ?? ""
          }`,
          value: user.id,
        }));
      setAddUserOpt(filtered);
    } else {
      setAddUserOpt([]);
      setSelectedUser("");
    }
  }, [dialogType, isAddUser, license]);

  useEffect(() => {
    if (addUserOpt.length === 0) setIsAvailableUsers(false);
    else setIsAvailableUsers(true);
  }, [addUserOpt]);

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новая лицензия";
      case "edit":
        return "Редактировать лицензию";
      case "delete":
        return "Удалить лицензию";
      case "enable":
        return "Активировать лицензию";
      case "disable":
        return "Деактивировать лицензию";
      case "addUser":
        return "Добавить пользователя";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const getButtonLabel = (type: string) => {
    switch (type) {
      case "create":
        return "Создать";
      case "edit":
        return "Сохранить";
      case "delete":
        return "Удалить";
      case "enable":
        return "Активировать";
      case "disable":
        return "Деактивировать";
      case "addUser":
        return "Добавить";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const footerContent = (
    <div>
      <Button label="Отмена" onClick={handleHide} outlined />
      <Button
        label={getButtonLabel(dialogType)}
        onClick={handleSubmit}
        loading={submitting}
        severity={dialogType === "disable" ? "danger" : undefined}
      />
    </div>
  );

  const usersDropdownForm = (
    <>
      {isAvailableUsers ? (
        <CustomDropdown
          value={selectedUser}
          onChange={(val) => setSelectedUser(val)}
          options={addUserOpt}
          placeholder="Пользователь"
          icon={<IconUser size={20} />}
          filter
        />
      ) : (
        <DangerText>В этой компании нет пользователей</DangerText>
      )}
    </>
  );

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {dialogType === "create" ? (
        <>
          <CustomInputNumberIncrement
            value={formData.numberOfLicenses}
            setValue={(val) => handleFieldChange("numberOfLicenses", val)}
            onBlur={() => handleBlur("numberOfLicenses")}
            placeholder="Количество лицензий"
            invalid={errors.numberOfLicenses}
          />
          <CustomDropdown
            value={formData.type}
            onChange={(val) => handleFieldChange("type", val)}
            onBlur={() => handleBlur("type")}
            options={LICENSE_TYPES}
            placeholder="Тип лицензии"
            icon={<IconDocument size={20} />}
            invalid={errors.type}
          />
          {project ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.projectId}
              onChange={(val) => handleFieldChange("projectId", val)}
              onBlur={() => handleBlur("projectId")}
              options={projectsOptions}
              placeholder="Проект"
              icon={<IconCase size={20} />}
              invalid={errors.projectId}
              filter
            />
          )}
          {company ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.organizationId}
              onChange={(val) => handleFieldChange("organizationId", val)}
              onBlur={() => handleBlur("organizationId")}
              options={companiesOptions}
              placeholder="Компания"
              icon={<IconCompany size={20} />}
              invalid={errors.organizationId}
              filter
            />
          )}
          <Row>
            <CustomCalendar
              key="start-date"
              value={formData.startDate}
              setValue={(date) => handleFieldChange("startDate", date)}
              onBlur={() => handleBlur("startDate")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startDate}
            />
            <BodyText>—</BodyText>
            <CustomCalendar
              key="end-date"
              value={formData.expirationDate}
              setValue={(date) => handleFieldChange("expirationDate", date)}
              onBlur={() => handleBlur("expirationDate")}
              placeholder="Дата окончания"
              icon={<IconCalendar size={20} />}
              invalid={errors.expirationDate}
            />
          </Row>
        </>
      ) : localLicense ? (
        <>
          <SmallColumn>
            <StyledIconLine
              icon={<IconCaseFilled size={20} />}
              text={enrichedLicense?.projectName ?? "Нет данных"}
            />
            <StyledIconLine
              icon={<IconCompanyFilled size={20} />}
              text={`${enrichedLicense?.organizationName ?? "Нет данных"}`}
            />
            {enrichedLicense?.userName &&
            enrichedLicense?.licenseType !== "Сессионная" ? (
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={`${
                  enrichedLicense?.userName
                    ? enrichedLicense?.userName === "—"
                      ? "Не добавлен"
                      : enrichedLicense?.userName
                    : "Нет добавлен"
                }`}
              />
            ) : (
              <></>
            )}
          </SmallColumn>
          <Row>
            <CustomCalendar
              value={formData.startDate}
              setValue={(date) => handleFieldChange("startDate", date)}
              onBlur={() => handleBlur("startDate")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startDate}
            />
            <BodyText>—</BodyText>
            <CustomCalendar
              value={formData.expirationDate}
              setValue={(date) => handleFieldChange("expirationDate", date)}
              onBlur={() => handleBlur("expirationDate")}
              placeholder="Дата окончания"
              icon={<IconCalendar size={20} />}
              invalid={errors.expirationDate}
            />
          </Row>
          <InputTextarea
            autoResize
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            cols={30}
            placeholder="Комментарий"
          />
          {license?.userId ? (
            <></>
          ) : (
            <>
              <Button
                outlined
                severity="secondary"
                onClick={() => setIsAddUser(true)}
                visible={!isAddUser}
              >
                <IconPlusBlack size={20} />
                <BodyText style={{ marginLeft: "5px" }}>
                  Добавить пользователя
                </BodyText>
              </Button>
              {isAddUser ? usersDropdownForm : <></>}
            </>
          )}
        </>
      ) : (
        <BodyText>Загрузка...</BodyText>
      )}
    </Column>
  );

  const addUserForm = (
    <Column>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={enrichedLicense?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${enrichedLicense?.organizationName ?? "Нет данных"}`}
      />
      {usersDropdownForm}
    </Column>
  );

  const infoForm = (
    <SmallColumn>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={enrichedLicense?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${enrichedLicense?.organizationName ?? "Нет данных"}`}
      />
      {enrichedLicense?.licenseType !== "Сессионная" && (
        <StyledIconLine
          icon={<IconUserFilled size={20} />}
          text={`${
            enrichedLicense?.userName
              ? enrichedLicense?.userName === "—"
                ? "Не добавлен"
                : enrichedLicense?.userName
              : "Нет добавлен"
          }`}
        />
      )}
      <StyledIconLine
        icon={<IconDocFilled size={20} />}
        text={
          enrichedLicense?.licenseType
            ? `${enrichedLicense?.licenseType} лицензия`
            : "Нет данных"
        }
      />
      <StyledIconLine
        icon={<IconCalendarCheckFilled size={20} />}
        text={`Дата начала: ${
          license?.startDate
            ? formatDate(license.startDate.toString())
            : "Нет данных"
        }`}
      />
      <StyledIconLine
        icon={<IconCalendarXFilled size={20} />}
        text={`Дата завершения: ${
          license?.expirationDate
            ? formatDate(license.expirationDate.toString())
            : "Нет данных"
        }`}
      />
    </SmallColumn>
  );

  const deleteForm = (
    <Column>
      <DangerText>Вы уверены, что хотите удалить данную лицензию?</DangerText>
      {infoForm}
    </Column>
  );

  const enableForm = (
    <Column>
      <BodyText>Активировать данную лицензию?</BodyText>
      {infoForm}
    </Column>
  );

  const disableForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите деактивировать данную лицензию?
      </DangerText>
      {infoForm}
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      case "enable":
        return enableForm;
      case "disable":
        return disableForm;
      case "addUser":
        return addUserForm;
      default:
        return <></>;
    }
  };

  return (
    <Dialog
      header={getHeader(dialogType)}
      visible={visible}
      style={{ width: "500px" }}
      onHide={handleHide}
      footer={footerContent}
    >
      {getContent(dialogType)}
    </Dialog>
  );
};

export default LicenseDialogs;
